import { BaseResponse } from "@/modules/types/response";

export interface GroupWeights {
  must_have: number;
  experience_depth: number;
  nice_to_have: number;
}

export interface MustHaveSkill {
  skill: string;
  min_years: number;
  weight: number;
}

export interface ExperienceDepthCriterion {
  domain: string;
  description: string;
  weight: number;
}

export interface NiceToHaveSkill {
  skill: string;
  weight: number;
}

export interface RubricJson {
  schema_version?: number;
  group_weights: GroupWeights;
  must_have_skills: MustHaveSkill[];
  experience_depth_criteria: ExperienceDepthCriterion[];
  nice_to_have_skills: NiceToHaveSkill[];
  red_flags: string[];
}

export interface JobScoringConfig {
  id?: number | string;
  job_id: number | string;
  rubric: RubricJson;
  autopilot_enabled: boolean;
  autopilot_threshold: number;
  needs_manual_review: boolean;
  is_manually_edited: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface UpdateJobScoringConfigPayload {
  rubric: RubricJson;
  autopilot_enabled: boolean;
  autopilot_threshold: number;
}

export interface JobScoringConfigResponse extends BaseResponse {
  id?: number | string;
  job_id?: number | string;
  rubric?: RubricJson;
  autopilot_enabled?: boolean;
  autopilot_threshold?: number;
  needs_manual_review?: boolean;
  is_manually_edited?: boolean;
  created_at?: string;
  updated_at?: string;
  /** "PROCESSING" when POST extract-rubric was accepted and is running in background (TOG-210). */
  extraction_status?: RubricExtractionStatus | "";
}

export type RubricExtractionStatus = "PROCESSING" | "SUCCESS" | "FAILED";

export const JOB_RUBRIC_EXTRACTION_EVENT = "job_rubric_extraction_finished";

/** SSE payload pushed by realtime-svc on channel business_{businessId} (TOG-210). */
export interface JobRubricExtractionEvent {
  event_type: typeof JOB_RUBRIC_EXTRACTION_EVENT;
  /** Job hex UID (same as in app routes). */
  job_id: string;
  status: Exclude<RubricExtractionStatus, "PROCESSING">;
  needs_manual_review?: boolean;
  error_code?: string;
  request_id?: string;
  finished_at?: string;
}

export function isJobRubricExtractionEvent(
  data: unknown,
): data is JobRubricExtractionEvent {
  return (
    !!data &&
    typeof data === "object" &&
    (data as { event_type?: unknown }).event_type === JOB_RUBRIC_EXTRACTION_EVENT &&
    typeof (data as { job_id?: unknown }).job_id === "string"
  );
}
